import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"

import { Button } from "@coval/ui"
import { useWavesurfer } from "@wavesurfer/react"
import { type RegionParams } from "wavesurfer.js/dist/plugins/regions.js"

import { logger } from "@/lib/logger"
import { useTranscriptParticipants } from "@/components/transcript/TranscriptParticipantsContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import type { WavesurferPeaks } from "@/hooks/usePrecomputedPeaks"
import type { RunRow } from "@/types/RunRow"
import { downloadBlob } from "@/utils/download"

import {
  AudioMarkerModal,
  CompactAudioControls,
  CompactAudioTime,
  FullAudioControls,
} from "./AudioPlayerControls"
import {
  AddAudioMarkerButton,
  AudioMarkerFlags,
  AudioRegionSelectionMenu,
} from "./AudioPlayerOverlays"
import {
  serializeAudioRegions,
  type AudioRegionParams,
} from "./audioRegionController"
import {
  SPEAKER_GUTTER_PIXELS,
  SpeakerLaneAvatars,
  SpeakerLaneBands,
} from "./AudioSpeakerLanes"
import { useAudioMarkerController } from "./useAudioMarkerController"
import { useAudioRegionSync } from "./useAudioRegionSync"
import { useAudioWaveformLifecycle } from "./useAudioWaveformLifecycle"
import { useAudioWaveformOptions } from "./useAudioWaveformOptions"

const log = logger("AudioPlayer")

export type { AudioRegionParams } from "./audioRegionController"

export interface AudioPlayerHandle {
  /** Returns the decoded AudioBuffer from the loaded audio, or null if not ready. */
  getDecodedData: () => AudioBuffer | null
}

interface AudioPlayerProps {
  time?: number
  compact?: boolean
  signedUrl?: string
  isErrorAudio?: boolean
  regions?: RegionParams[]
  /** When provided, regions are editable: drag/resize, click to delete, double-click empty to add */
  onRegionsChange?: (regions: AudioRegionParams[]) => void
  /** When true, editable regions keep full start/end durations instead of collapsing to markers */
  useRegionsForEditing?: boolean
  onRegionPlayed?: (region: AudioRegionParams) => void
  onIsPlayingChange?: (isPlaying: boolean) => void
  /** Called as playback position advances (seconds). Caller should throttle if needed. */
  onTimeUpdate?: (currentTimeSeconds: number) => void
  onReady?: () => void
  togglePlayPauseRequestId?: number
  simulationOutputId?: string
  humanEvalMode?: boolean
  currentSelection?: RunRow
  /** When provided, called instead of default add-region on waveform double-click. */
  onWaveformDoubleClick?: (
    time: number,
    position: { x: number; y: number },
  ) => void
  /** Extra content rendered inside the selected-region popup menu. */
  regionMenuExtra?: (regionId: string) => React.ReactNode
  /** When set, seek to start, play the range, and pause at end. Change requestId to re-trigger. */
  playRange?: { start: number; end: number; requestId: number }
  /** Precomputed waveform peaks for instant rendering before audio decodes. */
  precomputedPeaks?: WavesurferPeaks | null
  /** Audio duration in seconds — enables time display before audio decodes when peaks are provided. */
  audioDuration?: number | null
}

export const AudioPlayer = React.forwardRef<
  AudioPlayerHandle,
  AudioPlayerProps
>(
  (
    {
      time,
      compact = false,
      signedUrl,
      isErrorAudio,
      onRegionPlayed,
      onRegionsChange,
      useRegionsForEditing = false,
      regions,
      onIsPlayingChange,
      onTimeUpdate,
      onReady,
      togglePlayPauseRequestId,
      simulationOutputId,
      humanEvalMode = false,
      onWaveformDoubleClick,
      regionMenuExtra,
      playRange,
      precomputedPeaks,
      audioDuration,
    },
    ref,
  ) => {
    const idPrefix = useId()
    const containerRef = useRef<HTMLDivElement>(null)
    // The add-marker bubble is an absolute child of the player root, so its
    // hover coordinates have to be resolved against that same root.
    const rootRef = useRef<HTMLDivElement>(null)
    const [playbackRate, setPlaybackRate] = useState(1)
    const zoomLevel = 0
    const [isDownloading, setIsDownloading] = useState(false)

    const markerModalRef = useRef<HTMLDivElement>(null)
    const labelInputRef = useRef<HTMLInputElement>(null)

    const {
      cursorColor,
      height: waveformHeight,
      laneHeight,
      progressColor,
      splitChannels,
      waveColor,
    } = useAudioWaveformOptions(compact)
    const transcriptParticipants = useTranscriptParticipants()

    const loadablePrecomputedPeaks = useMemo(() => {
      if (!precomputedPeaks || !splitChannels) {
        return precomputedPeaks
      }

      // The current peaks service emits mono peaks. Passing mono peaks into a
      // split-channel player makes Wavesurfer render only one waveform lane, so
      // fall back to decoding the stereo audio until per-channel peaks exist.
      return precomputedPeaks.length >= splitChannels.length
        ? precomputedPeaks
        : null
    }, [precomputedPeaks, splitChannels])

    const { wavesurfer, isPlaying, isReady, currentTime } = useWavesurfer({
      container: containerRef as React.RefObject<HTMLElement>,
      waveColor,
      progressColor,
      normalize: true,
      minPxPerSec: zoomLevel,
      height: waveformHeight,
      cursorWidth: 2,
      cursorColor,
      dragToSeek: true,
      splitChannels,
    })

    // Only a recording that kept a channel per speaker can be coloured and
    // faced by speaker — a mono mixdown draws one lane that belongs to both.
    const hasSpeakerLanes =
      !compact &&
      isReady &&
      (wavesurfer?.getDecodedData()?.numberOfChannels ?? 0) >= 2
    const speakerAvatarParticipants = hasSpeakerLanes
      ? transcriptParticipants
      : undefined

    const { regionSelection, regionsPluginRef, styleResizeHandles } =
      useAudioRegionSync({
        compact,
        isReady,
        onRegionPlayed,
        onRegionsChange,
        regions,
        useRegionsForEditing,
        wavesurfer,
      })

    const {
      addMarker,
      buttonPosition,
      editingMarker,
      handleAddMarkerAtPosition,
      handleButtonMouseEnter,
      handleButtonMouseLeave,
      handleEditMarker,
      handleModalClose,
      handleUpdateMarker,
      handleWaveformMouseLeave,
      handleWaveformMouseMove,
      hoverTime,
      isButtonHovered,
      isHovering,
      manualTime,
      markers,
      newMarkerLabel,
      setHoverTime,
      setManualTime,
      setNewMarkerLabel,
      showAddMarkerModal,
    } = useAudioMarkerController({
      anchorRef: rootRef,
      containerRef,
      wavesurfer,
    })
    const { loadError, onPlayPause } = useAudioWaveformLifecycle({
      audioDuration,
      currentTime,
      isPlaying,
      isReady,
      loadablePrecomputedPeaks,
      onIsPlayingChange,
      onReady,
      onTimeUpdate,
      playRange,
      playbackRate,
      regions,
      signedUrl,
      time,
      togglePlayPauseRequestId,
      wavesurfer,
    })

    useImperativeHandle(
      ref,
      () => ({
        getDecodedData: () => wavesurfer?.getDecodedData() ?? null,
      }),
      [wavesurfer],
    )

    const handlePlaybackRateChange = useCallback(
      (newRate: number) => {
        setPlaybackRate(newRate)
        wavesurfer?.setPlaybackRate(newRate)
      },
      [wavesurfer],
    )

    const handleDownload = useCallback(async () => {
      if (!signedUrl) {
        return
      }

      try {
        setIsDownloading(true)

        // Fetch the audio file
        const response = await fetch(signedUrl)
        if (!response.ok) {
          throw new Error("Failed to fetch audio file")
        }

        const blob = await response.blob()

        // Determine file extension from content type
        let extension = "wav" // default
        if (blob.type) {
          if (blob.type.includes("wav")) {
            extension = "wav"
          } else if (blob.type.includes("m4a") || blob.type.includes("mp4")) {
            extension = "m4a"
          } else if (blob.type.includes("ogg")) {
            extension = "ogg"
          } else if (blob.type.includes("webm")) {
            extension = "webm"
          }
        }

        // Use simulation output ID as filename with correct extension
        const filename = simulationOutputId
          ? `${simulationOutputId}.${extension}`
          : `audio.${extension}`

        downloadBlob(blob, filename)
      } catch (error) {
        log.error("Error downloading audio", error)
      } finally {
        setIsDownloading(false)
      }
    }, [signedUrl, simulationOutputId])

    useEscapeShortcut({
      allowInInputs: true,
      description: "Close add marker dialog",
      enabled: showAddMarkerModal,
      handler: handleModalClose,
      priority: ESCAPE_SHORTCUT_PRIORITY.DIALOG,
    })

    const handleWaveformDoubleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!containerRef.current || !wavesurfer || !isReady) {
          return
        }
        const plugin = regionsPluginRef.current
        if (plugin && onRegionsChange) {
          const isOnRegion = plugin
            .getRegions()
            .some((r) => r.element?.contains(e.target as Node))
          if (isOnRegion) {
            return
          }
        }
        const rect = containerRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const duration = wavesurfer.getDuration()
        const time = Math.max(
          0,
          Math.min(duration, (x / rect.width) * duration),
        )

        // Delegate to parent when custom handler is provided (e.g. label picker)
        if (onWaveformDoubleClick) {
          onWaveformDoubleClick(time, { x: e.clientX, y: e.clientY })
          return
        }

        if (!plugin || !onRegionsChange) {
          return
        }

        const end = useRegionsForEditing ? Math.min(duration, time + 1) : time
        plugin.addRegion({
          start: time,
          end,
          color: "rgba(147, 51, 234, 0.5)",
          content: useRegionsForEditing
            ? `${(end - time).toFixed(2)}s`
            : undefined,
          drag: true,
          resize: useRegionsForEditing,
        })
        onRegionsChange(serializeAudioRegions(plugin.getRegions()))
        if (useRegionsForEditing) {
          requestAnimationFrame(() => requestAnimationFrame(styleResizeHandles))
        }
      },
      [
        onRegionsChange,
        onWaveformDoubleClick,
        regionsPluginRef,
        wavesurfer,
        isReady,
        useRegionsForEditing,
        styleResizeHandles,
      ],
    )

    const handleAddSegmentAtCurrentTime = useCallback(() => {
      if (!wavesurfer || !isReady) {
        return
      }

      const duration = wavesurfer.getDuration()
      const time = wavesurfer.getCurrentTime()

      if (onWaveformDoubleClick) {
        onWaveformDoubleClick(time, { x: 0, y: 0 })
        return
      }

      const plugin = regionsPluginRef.current
      if (!plugin || !onRegionsChange) {
        return
      }

      const end = useRegionsForEditing ? Math.min(duration, time + 1) : time
      plugin.addRegion({
        start: time,
        end,
        color: "rgba(147, 51, 234, 0.5)",
        content: useRegionsForEditing
          ? `${(end - time).toFixed(2)}s`
          : undefined,
        drag: true,
        resize: useRegionsForEditing,
      })
      onRegionsChange(serializeAudioRegions(plugin.getRegions()))
      if (useRegionsForEditing) {
        requestAnimationFrame(() => requestAnimationFrame(styleResizeHandles))
      }
    }, [
      onRegionsChange,
      onWaveformDoubleClick,
      regionsPluginRef,
      wavesurfer,
      isReady,
      useRegionsForEditing,
      styleResizeHandles,
    ])

    useEffect(() => {
      if (!showAddMarkerModal) {
        return
      }

      // Focus the label input when modal opens
      requestAnimationFrame(() => {
        labelInputRef.current?.focus()
      })

      const dialog = markerModalRef.current
      if (!dialog) {
        return
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        // Focus trap: cycle through focusable elements
        if (e.key === "Tab") {
          const focusable = dialog.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          )
          if (focusable.length === 0) {
            return
          }

          const first = focusable[0]
          const last = focusable[focusable.length - 1]

          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault()
              last.focus()
            }
          } else if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }

      dialog.addEventListener("keydown", handleKeyDown)
      return () => dialog.removeEventListener("keydown", handleKeyDown)
    }, [showAddMarkerModal])

    const elapsedSeconds = wavesurfer ? currentTime : null
    const totalSeconds = wavesurfer ? wavesurfer.getDuration() : null

    return (
      <div
        ref={rootRef}
        className={`${
          compact
            ? "flex items-center gap-4"
            : "flex flex-col rounded-md bg-surface-default"
        } relative`}
      >
        <AddAudioMarkerButton
          visible={humanEvalMode && (isHovering || isButtonHovered)}
          position={buttonPosition}
          onAdd={handleAddMarkerAtPosition}
          onMouseEnter={handleButtonMouseEnter}
          onMouseLeave={handleButtonMouseLeave}
        />
        <AudioMarkerModal
          canSubmit={Boolean(wavesurfer)}
          currentTime={wavesurfer?.getCurrentTime() ?? 0}
          dialogRef={markerModalRef}
          hoverTime={hoverTime}
          idPrefix={idPrefix}
          isEditing={editingMarker !== null}
          label={newMarkerLabel}
          labelInputRef={labelInputRef}
          manualTime={manualTime}
          onClose={handleModalClose}
          onHoverTimeChange={setHoverTime}
          onLabelChange={setNewMarkerLabel}
          onSubmit={editingMarker !== null ? handleUpdateMarker : addMarker}
          onTimeChange={setManualTime}
          open={showAddMarkerModal}
        />

        {compact && signedUrl && wavesurfer && (
          <CompactAudioControls
            isDownloading={isDownloading}
            isPlaying={isPlaying}
            onDownload={() => {
              void handleDownload()
            }}
            onPlayPause={onPlayPause}
            onPlaybackRateChange={handlePlaybackRateChange}
            playbackRate={playbackRate}
          />
        )}
        <div
          className={`flex w-full min-w-0 items-center ${
            compact ? "gap-3" : "px-4 pt-3"
          }`}
        >
          {speakerAvatarParticipants && (
            <SpeakerLaneAvatars
              laneHeight={laneHeight}
              participants={speakerAvatarParticipants}
            />
          )}
          <div
            className={`overflow-hidden relative w-full flex-1 ${
              compact ? "h-[40px]" : "h-[60px] rounded-md"
            } ${
              // The per-speaker bands cover the well themselves, and leave a gap
              // between them that only shows while the well draws no fill.
              compact || hasSpeakerLanes ? "" : "bg-coval-media-track"
            } pt-1 min-w-0`}
            onMouseMove={handleWaveformMouseMove}
            onMouseLeave={handleWaveformMouseLeave}
            onDoubleClick={
              onRegionsChange || onWaveformDoubleClick
                ? handleWaveformDoubleClick
                : undefined
            }
          >
            {onRegionsChange && (
              <AudioRegionSelectionMenu
                selectedRegionId={regionSelection.selectedRegionId}
                selectedPosition={regionSelection.selectedPosition}
                clampedPosition={regionSelection.clampedPosition}
                menuRef={regionSelection.menuRef}
                extra={regionMenuExtra}
                onDelete={regionSelection.deleteSelectedRegion}
                onClear={regionSelection.clearSelection}
              />
            )}

            {/* Centred rather than top-aligned, so the well's extra height reads
                as breathing room above and below the waveform. */}
            <div className="absolute inset-0 flex w-full items-center">
              <div
                className="relative w-full"
                style={compact ? undefined : { height: waveformHeight }}
              >
                {hasSpeakerLanes && (
                  <SpeakerLaneBands laneHeight={laneHeight} />
                )}
                <div
                  ref={containerRef}
                  className={compact ? "w-full" : "absolute inset-0 w-full"}
                />
              </div>
            </div>

            <AudioMarkerFlags
              markers={humanEvalMode ? markers : []}
              duration={wavesurfer?.getDuration() ?? 0}
              onEdit={handleEditMarker}
            />
            {(onRegionsChange || onWaveformDoubleClick) && (
              <Button
                variant="ghost"
                className="sr-only focus:not-sr-only focus:absolute focus:z-10"
                onClick={handleAddSegmentAtCurrentTime}
                aria-label="Add segment at current time"
              >
                Add segment at current time
              </Button>
            )}
          </div>
        </div>

        {(isErrorAudio || loadError) && (
          <div className="flex justify-center items-center h-24">
            <p>Error loading audio</p>
          </div>
        )}
        {signedUrl && wavesurfer && !loadError && (
          <>
            {compact ? (
              <CompactAudioTime
                currentTime={currentTime}
                duration={wavesurfer.getDuration()}
              />
            ) : (
              <FullAudioControls
                leadingInsetPixels={
                  speakerAvatarParticipants ? SPEAKER_GUTTER_PIXELS : 0
                }
                elapsedSeconds={elapsedSeconds}
                totalSeconds={totalSeconds}
                isDownloading={isDownloading}
                isPlaying={isPlaying}
                onDownload={() => {
                  void handleDownload()
                }}
                onPlayPause={onPlayPause}
                onPlaybackRateChange={handlePlaybackRateChange}
                playbackRate={playbackRate}
              />
            )}
          </>
        )}
      </div>
    )
  },
)
AudioPlayer.displayName = "AudioPlayer"
